import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ChangePassword,
  ConfirmPassword,
  PasswordStrengthMeter,
} from 'templates';
import {
  setStrengthBarColor,
  sessionTimedOut,
  userLockedOut,
  NavigateConst,
} from 'common';
import { PasswordInfoOverlay } from 'pages';
import {
  useForgetPassword,
  useForgetPasswordToken,
} from '@sentara-health/sentara-api-hooks-core';
import {
  Button,
  CheckIconBig,
  Headings,
  ModalOverlay,
  HelpIcon,
  GlobalError,
  Paragraph,
} from '@sentara-health/optimahealth_web';

import { errorCodeToStateUpdate, PASSWORD_CRITERIA } from './PasswordConstant';
import { useForm, FormProvider } from 'react-hook-form';
import { GlobalContext } from 'context';

interface FormInputs {
  password: string;
  confirmPassword: string;
}
// Define the interface for the props passed to PasswordChange component

function ResetPassword({ params, authId = '' }: any) {
  const { t } = useTranslation();
  const methods = useForm<FormInputs>({});
  const {
    handleSubmit,
    formState: { isValid },
  } = methods;
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const [resetData, setResetData] = useState<any>({});
  const { forgetPassword } = useForgetPassword();
  const [pwdStrength, setPwdStrength] = useState(0);
  const [value, setValue] = useState<any>('');
  const [retriveSuccess, setRetriveSuccess] = useState(false);
  const [noContent, setNoContent] = useState(false);
  const [unauthError, setUnauthError] = useState(false);
  const [tokenExpiry, setTokenExpiry] = useState(false);
  const [passCriteria, setPassCriteria] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [userLockoutError, setUserLockoutError] = useState(false);
  const { forgetPasswordToken } = useForgetPasswordToken();
  const [authIdValue, setAuthIdValue] = useState<string>('');
  const { getFeature } = useContext(GlobalContext);

  useEffect(() => {
    if (typeof getFeature?.isModernizedAPI === 'boolean') {
      (async () => {
        if (params !== null) {
          let tokenId = { encryptedValue: params };
          const response: any = await forgetPasswordToken(tokenId);
          if (response?.errorCode) {
            const unAuthorizedError = [400, 401, 404, 410, 462, 500];
            const unAuthError = unAuthorizedError.includes(
              Number(response?.errorCode)
            );
            if (unAuthError) {
              setTokenExpiry(true);
            }
          } else {
            setAuthIdValue(response?.data?.authId);
            setTokenExpiry(false);
          }
        }
      })();
    }
  }, [params, getFeature?.isModernizedAPI]);

  useEffect(() => {
    setStrengthBarColor(value, setPwdStrength);
  }, [value]);

  useEffect(() => {
    return setResetData({
      user: {
        NewPassword: value,
        token: params,
        authId: authIdValue,
      },
      channel: 'Web',
      lang: 'en',
    });
  }, [value]);

  /* oncancel click navigate to sign in */
  const cancelClick = () => {
    navigate(NavigateConst.signIn);
  };

  // Render global error
  const renderGlobalError = (condition: boolean, messageKey: string) => {
    return condition ? (
      <GlobalError variant="my-4" dataTestId={'confirmPasswordInputError'}>
        {t(messageKey)}
      </GlobalError>
    ) : null;
  };

  // Handle form submission
  const formSubmitHandler = async (data: FormInputs) => {
    if (pwdStrength === 4) {
      await handleForgetPassword();
    }
  };

  function openModalPassword() {
    setIsPasswordModalOpen(true);
  }
  function closeModalPassword() {
    setIsPasswordModalOpen(false);
  }
  const handleForgetPassword = async () => {
    let response = await forgetPassword(resetData);
    const errorToStateUpdate = errorCodeToStateUpdate(
      setNoContent,
      setUnauthError,
      setPassCriteria,
      setTokenExpiry,
      setServerError
    );
    if (response?.errorCode) {
      if (
        response?.errorDetails &&
        response?.errorDetails?.message === sessionTimedOut
      ) {
        setTokenExpiry(true);
      } else if (
        response?.errorDetails &&
        response?.errorDetails === userLockedOut
      ) {
        setUserLockoutError(true); // Set user locked out error
        setPassCriteria(false); // Unset password criteria error
      } else if (response.errorCode in errorToStateUpdate) {
        errorToStateUpdate[
          response.errorCode as keyof typeof errorToStateUpdate
        ](true);
      } else {
        setServerError(true);
      }
    } else {
      setRetriveSuccess(true);
    }
  };

  // Handle key down event
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Space') {
      e.preventDefault();
    }
  };

  const inputStrengthClass =
    pwdStrength in PASSWORD_CRITERIA
      ? `strength-bar-${PASSWORD_CRITERIA[pwdStrength]}`
      : '';

  return (
    <>
      <div className="row">
        <div className="col-12">
          <Headings
            level={1}
            text={t('resetPassword.header')}
            variant="semiBold"
            dataTestId="userNameForgot"
            className={`mt-5 ${(retriveSuccess || tokenExpiry) === true
                ? 'text-center mb-0'
                : 'pb-2'
              }`}
          />
          {!retriveSuccess && !tokenExpiry && params !== null ? (
            <>
              <div className="mb-4">{t('resetPassword.resetPasswordInfo')}</div>
              {renderGlobalError(
                noContent,
                'resetPassword.passwordServiceErr1'
              )}
              {renderGlobalError(
                unauthError,
                'resetPassword.passwordServiceErr2'
              )}
              {renderGlobalError(passCriteria, 'common.passwordrequired')}
              {renderGlobalError(serverError, 'common.activationError4')}
              {renderGlobalError(
                userLockoutError,
                'common.accountLockoutMessage'
              )}
              <FormProvider {...methods}>
                <form
                  data-testid="passwordInputLabelContainer"
                  onSubmit={handleSubmit(formSubmitHandler)}
                >
                  <div className="pb-4">
                    {/* Render ChangePassword component */}
                    <div className="d-flex align-items-center">
                      <div className="position-relative flex-1">
                        <ChangePassword
                          onKeyDown={onKeyDown}
                          setValue={setValue}
                          setNoContent={setNoContent}
                          setUnauthError={setUnauthError}
                          setPassCriteria={setPassCriteria}
                          setServerError={setServerError}
                          showPasswordPlaceholder={`${t(
                            'common.passwordPlaceholder'
                          )}`}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Render PasswordStrengthMeter component */}
                  <div className="pb-2">
                    <PasswordStrengthMeter
                      inputStrengthClass={inputStrengthClass}
                    />
                  </div>

                  {/* Render ConfirmPassword component */}
                  <div className="pb-2">
                    <ConfirmPassword
                      onKeyDown={onKeyDown}
                      showPasswordPlaceholder={''}
                    />
                  </div>
                  <hr />
                  <div className="row pt-4 pb-2">
                    <div className="col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
                      <Button
                        dataTestId="submitButton"
                        variant="primary_btn"
                        className="btn-md-width"
                        type="submit"
                      >
                        {t('common.continue')}
                      </Button>
                    </div>
                    <div className="col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
                      <Button
                        dataTestId="cancelButton"
                        type="button"
                        onClick={cancelClick}
                        variant="secondary_btn"
                        className="btn-md-width"
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </>
          ) : (
            <>
              {/* success content */}
              <div className="row justify-content-center pt-4">
                <div className="col-12 text-center">
                  {retriveSuccess && !tokenExpiry ? (
                    <>
                      <div className="mb-3 pb-1">
                        <CheckIconBig />
                      </div>
                      <Headings
                        level={1}
                        text={t('common.activationSuccess')}
                        dataTestId="alertShowSuccessMessage"
                        variant="semiBold"
                        className="mb-3 pb-1"
                      />
                        <Paragraph
                          text={t('resetPassword.passwordResetSuccess')}
                          variant="regular"
                          className="mb-5"
                          data-testid="resetLinkText"
                        />
                    </>
                  ) : (
                        <Paragraph
                          text={t('common.showTokenError')}
                          variant="regular"
                          className="mb-5 lineBrake"
                          data-testid="resetText"
                        />
                  )}
                  <div className="pt-3">
                    <Button
                      onClick={() => {
                        navigate(NavigateConst.signIn);
                      }}
                      type="button"
                      dataTestId="redirectLoginPage"
                        variant="primary_btn"
                        className="btn-md-width"
                    >
                      {t('common.returnLogin')}
                    </Button>
                  </div>
                </div>
              </div>
              {/* success content */}
            </>
          )}
        </div>
      </div>
    </>
  );
}
export default ResetPassword;